export class AppContext {
	_urlPrefix
	_menu
	_processInstanceId
	_pendingStoreName

	/**
	 * Only one context should be active at a time
	 * open == true means it is visible and can be clicked
	 * If the anchor (<a>) of the menu item (in Menu.js) has the attribute alwaysVisible 
	 * it is still visible but disabled when open == false
	 */
	activityTree = {
		stores: {
			active: true,
			open: true,
			products: {
				active: false,
				open: false,
				cart: {
					active: false,
					open: false,
					checkout: {
						active: false,
						open: false,
						confirmation: {
							active: false,
							open: false,
						}
					}
				}
			}
		},
		aboutUs: {
			active: false,
			open: true
		},
		myorders: {
			active: false,
			open: false
		},
		login: {
			active: false,
			open: false,
			account: {
				active: false,
				open: false
			}
		}
	}

	/**
	 * Lookup for the contect in the tree above
	 */
	activities = {
		stores: this.activityTree.stores,
		products: this.activityTree.stores.products,
		cart: this.activityTree.stores.products.cart,
		checkout: this.activityTree.stores.products.cart.checkout,
		confirmation: this.activityTree.stores.products.cart.checkout.confirmation,
		aboutUs: this.activityTree.aboutUs,
		myorders: this.activityTree.myorders,
		login: this.activityTree.login,
		account: this.activityTree.login.account
	}

	constructor() {
		this._urlPrefix = ''

		window.addEventListener('popstate', ev => {
			if (ev.state) {
				this._setActive(ev.state.activity)
			}
		})
	}

	restore = () => {
		const segments = window.location.pathname.split('/').filter(Boolean)
		const knownActivities = Object.keys(this.activities)

		// Detect /{storeName}/products — store name is not a known activity key
		const isStoreProducts = segments.length >= 2
			&& segments[1] === 'products'
			&& !knownActivities.includes(segments[0])

		// Confirmation processInstanceId: from URL (/confirmation/:uuid) or localStorage
		if (!isStoreProducts && segments[0] === 'confirmation' && segments[1]) {
			this._processInstanceId = segments[1]
		}
		else {
			this._processInstanceId = localStorage.getItem('processInstanceId')
		}

		const hasOrderHistory = JSON.parse(localStorage.getItem('orderHistory') || '[]').length > 0
		if (this._processInstanceId) {
			const confirmation = this.activities['confirmation'].components[0]
			confirmation.setProcessInstanceId(this._processInstanceId)
			confirmation.setLoadedByUrl(true)
			this.setOpen('confirmation', true)
			this.setOpen('myorders', true)
		}
		else {
			this.setOpen('confirmation', false)
			if (hasOrderHistory) {
				this.setOpen('myorders', true)
			}
		}

		if (isStoreProducts) {
			this._pendingStoreName = decodeURIComponent(segments[0])
			this.activities['products'].open = true
			this.setActive('products', null, this._pendingStoreName)
		}
		else if (segments.length > 0) {
			console.log('Init context:', segments[0])
			const activity = this.activities[segments[0]]
			if (activity && activity.open === true) {
				this.setActive(segments[0], this._processInstanceId)
			}
			else {
				this.setActive('stores')
			}
		}
		else {
			this.setActive('stores')
		}
	}

	setMenu = menu => this._menu = menu

	setComponents = (activity, components) => this.activities[activity].components = components

	setProcessInstanceId = processInstanceId => {
		this._processInstanceId = processInstanceId
		localStorage.setItem('processInstanceId', processInstanceId)
	}

	/**
	 * Called by menu to see if activity is currently active
	 * 
	 * @param {*} activity 
	 * @returns 
	 */
	isActive = activity => this.activities[activity].active

	/**
	 * Called by menu to update context and set component visible.
	 * Only one activity can be active.
	 * Adds the activity to history stack.
	 * 
	 * @param {*} activity 
	 */
	setActive = (activity, id, storeName) => {
		this._setActive(activity)
		const effectiveId = id ?? (activity === 'confirmation' ? this._processInstanceId : null)
		const url = (activity === 'products' && storeName)
			? `${this._urlPrefix}/${encodeURIComponent(storeName)}/products`
			: `${this._urlPrefix}/${activity}${effectiveId ? `/${effectiveId}` : ''}`
		console.log('Adding to history:', activity, url)
		window.history.pushState({ activity: activity }, '', url)
	}

	_setActive = activity => {
		// Deactivate and hide all activities
		Object.values(this.activities).forEach(a => {
			a.active = false
			if (a.components) {
				a.components.forEach(c => c.setVisible(false))
			}
		})
		// Activate and show components of current context
		if (this.activities[activity].components) {
			this.activities[activity].components.forEach(c => {
				c.setVisible(true)
			})
		}
		// Update menu
		this.activities[activity].active = true
		this._menu.setActive(activity)
		// Follow url
		console.debug('Navigated to: ', activity)
	}

	/**
	 * Called by menu to see if menu entry can be clicked
	 * @param {*} activity 
	 * @returns 
	 */
	isOpen = activity => this.activities[activity].open

	setOpen = (activity, open) => {
		this.activities[activity].open = open
		this._menu.setOpen(activity, open)
		// TODO react when the activity is active
	}
}